import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  BIT8_SUFFIX,
  CLASSIC_MODEL_ID,
  LEGACY_MARKERS,
  VEHICLE_MODELS,
  modelSideUrl,
  modelTopUrl,
  resolveVehicleModel,
  type VehicleCategory,
  type VehicleModel,
} from "../../config/vehicleModels";
import { savePointerModel, useVehicleGarage } from "../../features/vehicles/pointerModel";
import { useTheme } from "../../context/ThemeContext";
import ClassicV4Marker from "../../pages/home/components/ClassicV4Marker";
import { brand } from "../../config/theme";

// Garaje para elegir el vehículo de una unidad, al estilo del garaje de un
// juego de autos: un escenario con el vehículo bajo un foco, sus datos, cómo
// se ve en el mapa, y el carrusel de modelos abajo. Con los colores del tema
// (era siempre oscuro y desentonaba con la app) y con teclado:
// ← → para recorrer, Enter para elegir, Esc para salir.

const CATEGORIES: { id: "todos" | VehicleCategory | "nostalgia"; label: string }[] = [
  { id: "todos", label: "Todos" },
  { id: "tracto", label: "Tractos" },
  { id: "tracto-remolque", label: "Con remolque" },
  { id: "camion", label: "Camiones" },
  { id: "pickup", label: "Pickups" },
  { id: "auto", label: "Autos" },
  { id: "maquinaria", label: "Maquinaria" },
  { id: "nostalgia", label: "Nostalgia" },
];
const CATEGORY_LABEL: Record<VehicleCategory, string> = {
  tracto: "Tractocamión",
  "tracto-remolque": "Tractocamión con remolque",
  camion: "Camión",
  pickup: "Pickup",
  auto: "Auto",
  maquinaria: "Maquinaria",
  nostalgia: "Nostalgia · Dumax v2",
};
const MAX_LEN = Math.max(...VEHICLE_MODELS.map((m) => m.lengthM));
const BY_ID = new Map(VEHICLE_MODELS.map((m) => [m.id, m]));
// El carrusel lista cada vehículo una vez; su 8-bit se elige en el escenario.
const LISTED = VEHICLE_MODELS.filter((m) => !m.pixel);

// legacy = marcador de una plataforma anterior (va al final, "nostalgia").
type Option = { id: string; model: VehicleModel | null; legacy?: string };

export default function VehicleGarageHost() {
  const target = useVehicleGarage((s) => s.target);
  const close = useVehicleGarage((s) => s.close);
  if (!target) return null;
  return createPortal(<Garage key={target.vehicleId} onClose={close} {...target} />, document.body);
}

function Garage({
  vehicleId,
  name,
  bodyType,
  current = "",
  onClose,
}: {
  vehicleId: string;
  name: string;
  bodyType?: string;
  current?: string;
  onClose: () => void;
}) {
  const [cat, setCat] = useState<(typeof CATEGORIES)[number]["id"]>("todos");
  const options: Option[] = useMemo(
    () => [
      ...(cat === "todos" ? [{ id: CLASSIC_MODEL_ID, model: null }] : []),
      ...LISTED.filter((m) => m.category !== "nostalgia" && (cat === "todos" || m.category === cat)).map((m) => ({ id: m.id, model: m })),
      // Nostalgia al final: el marcador de v4 y los vehículos de v2.
      ...(cat === "todos" || cat === "nostalgia" ? LEGACY_MARKERS.map((m) => ({ id: m.id, model: null, legacy: m.name })) : []),
      ...LISTED.filter((m) => m.category === "nostalgia" && (cat === "todos" || cat === "nostalgia")).map((m) => ({ id: m.id, model: m })),
    ],
    [cat],
  );
  // Lo que se ve hoy: el elegido, o el de omisión de su carrocería.
  const effective = current || resolveVehicleModel("", bodyType)?.id || CLASSIC_MODEL_ID;
  const [selected, setSelected] = useState<string>(effective.replace(BIT8_SUFFIX, ""));
  // Remaster u original 8-bit (sólo los de v2 tienen las dos versiones).
  const [bit8, setBit8] = useState(effective.endsWith(BIT8_SUFFIX));
  const [dir, setDir] = useState<1 | -1>(1);
  const stripRef = useRef<HTMLDivElement>(null);

  // Colores del tema de la app (no un oscuro propio): claro u oscuro igual
  // que el resto, para que el garaje no se sienta de otra aplicación.
  const { isDarkMode } = useTheme();
  const k = isDarkMode
    ? {
        panel: "bg-dark-surface border-dark-border text-gray-100",
        stage: "bg-dark-card",
        tile: "bg-dark-card ring-dark-border [@media(hover:hover)]:hover:ring-gray-500",
        chipOn: "bg-primary-500 text-gray-900",
        chipOff: "bg-dark-card text-gray-300 hover:bg-dark-hover",
        navBtn: "bg-dark-surface/80 text-gray-200 hover:bg-dark-hover",
        iconBtn: "text-gray-400 hover:text-white hover:bg-dark-hover",
        badge: "bg-dark-surface text-gray-300",
        track: "bg-dark-border",
        line: "border-dark-border",
        muted: "text-gray-300",
        faint: "text-gray-500",
      }
    : {
        panel: "bg-white border-gray-200 text-gray-900",
        stage: "bg-gray-50",
        tile: "bg-gray-50 ring-gray-200 [@media(hover:hover)]:hover:ring-gray-300",
        chipOn: "bg-primary-500 text-gray-900",
        chipOff: "bg-gray-100 text-gray-600 hover:bg-gray-200",
        navBtn: "bg-white/90 text-gray-700 shadow-sm hover:bg-white",
        iconBtn: "text-gray-500 hover:text-gray-900 hover:bg-gray-100",
        badge: "bg-white text-gray-600 shadow-sm",
        track: "bg-gray-200",
        line: "border-gray-100",
        muted: "text-gray-700",
        faint: "text-gray-500",
      };

  const idx = Math.max(0, options.findIndex((o) => o.id === selected));
  const sel = options[idx] ?? options[0];
  const model8 = sel.model ? BY_ID.get(sel.model.id + BIT8_SUFFIX) : undefined;
  const shown = (bit8 && model8) || sel.model;
  const finalId = shown?.id ?? sel.id;

  const move = (d: 1 | -1) => {
    const next = options[(idx + d + options.length) % options.length];
    setDir(d);
    setSelected(next.id);
  };
  const choose = () => {
    // "Clásico" se guarda como tal; elegir el que ya daba la carrocería
    // también se guarda (queda fijo aunque cambie la carrocería).
    // Se cierra sin esperar al servidor: el cambio local es inmediato y el
    // PUT (Cloud Function, segundos en frío) dejaba el garaje tapando el
    // mapa con "Guardando…". Si falla, savePointerModel regresa al anterior
    // y avisa.
    if (finalId !== effective) void savePointerModel(vehicleId, finalId, current);
    onClose();
  };

  // Precargar la vista cenital del que se está mirando: al elegirlo, el
  // puntero del mapa ya la tiene en caché y cambia sin esperar la descarga.
  useEffect(() => {
    if (shown) new Image().src = modelTopUrl(shown.id);
  }, [shown]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") move(1);
      else if (e.key === "ArrowLeft") move(-1);
      else if (e.key === "Enter") choose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Rueda del mouse = desplazar el carrusel de lado (con 27 opciones, ir de
  // una en una con las flechas era lento). Sin passive para poder evitar que
  // la rueda desplace la página detrás del garaje. "instant": con el
  // scroll-behavior smooth cada evento reiniciaba la animación y se perdía
  // avance.
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // trackpad lateral: nativo
      e.preventDefault();
      el.scrollBy({ left: e.deltaY, behavior: "instant" });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Mantener la tarjeta elegida a la vista en el carrusel.
  useEffect(() => {
    stripRef.current?.querySelector<HTMLElement>(`[data-opt="${selected}"]`)?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [selected, cat]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6" role="dialog" aria-modal="true" aria-label={`Garaje · ${name}`}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm motion-safe:animate-fadeIn" onClick={onClose} />
      <div className={`relative w-full max-w-3xl max-h-full overflow-hidden rounded-2xl border shadow-2xl motion-safe:animate-scaleIn flex flex-col ${k.panel}`}>
        {/* Barra superior */}
        <div className="flex items-center gap-3 px-5 pt-4">
          <i className={`ri-steering-2-line text-lg ${k.faint}`} aria-hidden />
          <div className="min-w-0">
            <p className={`text-[11px] uppercase tracking-[0.18em] ${k.faint}`}>Garaje</p>
            <h2 className="font-mono font-semibold truncate">{name}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className={`ml-auto w-9 h-9 flex items-center justify-center rounded-lg ${k.iconBtn}`}>
            <i className="ri-close-line text-xl" aria-hidden />
          </button>
        </div>

        {/* Escenario */}
        <div className={`relative mx-5 mt-3 h-[180px] sm:h-[200px] rounded-xl overflow-hidden ${k.stage}`}
          style={{ backgroundImage: `radial-gradient(70% 90% at 50% 105%, ${brand.primary}2E, transparent 70%)` }}>
          {/* Piso con líneas de perspectiva sutiles */}
          <div aria-hidden className="absolute inset-x-0 bottom-0 h-1/3 opacity-30"
            style={{ background: `repeating-linear-gradient(90deg, transparent 0 46px, ${isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)"} 46px 47px)`, maskImage: "linear-gradient(to top, black, transparent)" }} />
          <button type="button" onClick={() => move(-1)} aria-label="Anterior" className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center ${k.navBtn}`}>
            <i className="ri-arrow-left-s-line text-2xl" aria-hidden />
          </button>
          <button type="button" onClick={() => move(1)} aria-label="Siguiente" className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center ${k.navBtn}`}>
            <i className="ri-arrow-right-s-line text-2xl" aria-hidden />
          </button>

          <div key={sel.id} className="absolute inset-0 motion-safe:animate-[garageIn_320ms_cubic-bezier(0.22,1,0.36,1)]" style={{ ["--gx" as string]: `${dir * 36}px` }}>
            <span aria-hidden className="absolute left-1/2 bottom-[26px] -translate-x-1/2 w-[42%] h-[14px] rounded-[50%]"
              style={{ background: `radial-gradient(closest-side, rgba(0,0,0,${isDarkMode ? 0.75 : 0.35}), rgba(0,0,0,0))`, filter: "blur(4px)" }} />
            {shown ? (
              <img key={shown.id} src={modelSideUrl(shown.id)} alt={shown.name} draggable={false}
                style={shown.pixel ? { imageRendering: "pixelated" } : undefined}
                className="absolute left-1/2 bottom-[29px] -translate-x-1/2 h-[118px] sm:h-[138px] max-w-[66%] object-contain select-none" />
            ) : sel.legacy ? (
              // Los 4 estados del marcador de v4, para reconocerlo de un vistazo.
              <div className="absolute inset-x-0 bottom-[40px] flex items-end justify-center gap-8">
                {(["driving", "ralenti", "stopped", "offline"] as const).map((st) => (
                  <div key={st} className="flex flex-col items-center gap-2">
                    <div className="scale-[1.8] origin-bottom"><ClassicV4Marker status={st} course={35} /></div>
                    <span className={`text-[10px] ${k.faint}`}>
                      {{ driving: "En ruta", ralenti: "Ralentí", stopped: "Detenida", offline: "Sin señal" }[st]}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`absolute left-1/2 bottom-[46px] -translate-x-1/2 flex flex-col items-center gap-3 ${k.muted}`}>
                <ClassicGlyph size={72} />
                <span className={`text-xs ${k.faint}`}>El marcador de siempre</span>
              </div>
            )}
          </div>

          {/* Remaster / original 8-bit (vehículos de v2) */}
          {model8 && (
            <div role="group" aria-label="Versión" className={`absolute left-3 top-3 z-10 flex p-0.5 rounded-lg text-[11px] font-medium ${k.badge}`}>
              {([[false, "Remaster"], [true, "Original 8-bit"]] as const).map(([v, label]) => (
                <button key={label} type="button" aria-pressed={bit8 === v} onClick={() => setBit8(v)}
                  className={`h-7 px-2.5 rounded-md transition-colors ${bit8 === v ? "bg-primary-500 text-gray-900" : ""}`}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Vista en el mapa (cenital) */}
          {shown && (
            <div className="absolute right-3 top-3" title="Así se ve en el mapa">
              <div className={`w-10 h-16 rounded-lg ring-1 flex items-center justify-center ${k.tile}`}>
                <img src={modelTopUrl(shown.id)} alt="Así se ve en el mapa" style={shown.pixel ? { imageRendering: "pixelated" } : undefined} className="max-h-12 max-w-7 object-contain rotate-[25deg]" />
              </div>
            </div>
          )}
        </div>

        {/* Ficha del modelo */}
        <div className="px-5 pt-3 flex items-end gap-6">
          <div className="min-w-0">
            <p className={`text-[11px] uppercase tracking-[0.18em] ${k.faint}`}>
              {sel.model ? CATEGORY_LABEL[sel.model.category] : sel.legacy ? "Nostalgia · Dumax v4" : "Predeterminado"}
            </p>
            <h3 className="text-xl font-semibold tracking-tight truncate">{sel.model ? sel.model.name : sel.legacy ?? "Clásico"}</h3>
          </div>
          {sel.model && (
            <div className="ml-auto w-48 shrink-0">
              <div className={`flex justify-between text-[11px] mb-1 ${k.faint}`}>
                <span>Largo</span>
                <span className={`tabular-nums ${k.muted}`}>{sel.model.lengthM} m</span>
              </div>
              <div className={`h-1.5 rounded-full overflow-hidden ${k.track}`}>
                <div className="h-full rounded-full bg-primary-500 transition-[width] duration-300 ease-out" style={{ width: `${(sel.model.lengthM / MAX_LEN) * 100}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* Categorías */}
        <div className="px-5 pt-4 flex gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {CATEGORIES.map((c) => (
            <button key={c.id} type="button" onClick={() => setCat(c.id)}
              className={`shrink-0 h-8 px-3 rounded-full text-xs font-medium transition-colors ${cat === c.id ? k.chipOn : k.chipOff}`}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Carrusel */}
        <div ref={stripRef} className={`px-5 pt-3 pb-2 mb-1 flex gap-2 overflow-x-auto [scrollbar-width:thin] ${isDarkMode ? "[scrollbar-color:#4b5563_transparent]" : "[scrollbar-color:#d1d5db_transparent]"}`}>
          {options.map((o) => {
            const on = o.id === selected;
            return (
              <button key={o.id} type="button" data-opt={o.id} onClick={() => { setDir(options.findIndex((x) => x.id === o.id) >= idx ? 1 : -1); setSelected(o.id); }}
                onDoubleClick={choose}
                aria-pressed={on}
                className={`relative shrink-0 w-[104px] h-[74px] rounded-xl overflow-hidden transition-[transform,box-shadow] duration-200 ease-out ${on ? "ring-2 ring-primary-500 -translate-y-0.5" : `ring-1 [@media(hover:hover)]:hover:-translate-y-0.5 ${k.tile}`}`}>
                {(o.id === current || o.id + BIT8_SUFFIX === current) && <span className={`absolute left-1.5 top-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${k.badge}`}>Actual</span>}
                {o.model ? (
                  <>
                    <span aria-hidden className="absolute left-1/2 bottom-[13px] -translate-x-1/2 w-[62%] h-[5px] rounded-[50%]" style={{ background: `radial-gradient(closest-side, rgba(0,0,0,${isDarkMode ? 0.6 : 0.3}), transparent)`, filter: "blur(2px)" }} />
                    <img src={modelSideUrl(o.model.id)} alt="" loading="lazy" draggable={false} className="absolute left-1/2 bottom-[15px] -translate-x-1/2 h-[38px] max-w-[88%] object-contain" />
                  </>
                ) : o.legacy ? (
                  <span className="absolute inset-x-0 top-3 flex justify-center gap-1.5">
                    <ClassicV4Marker status="driving" course={35} size={18} />
                    <ClassicV4Marker status="stopped" course={0} size={18} />
                  </span>
                ) : (
                  <span className={`absolute inset-x-0 top-3 flex justify-center ${k.muted}`}><ClassicGlyph size={34} /></span>
                )}
                <span className={`absolute inset-x-1.5 bottom-1 text-[10px] leading-tight truncate text-center ${k.muted}`}>{o.model ? o.model.name : o.legacy ?? "Clásico"}</span>
              </button>
            );
          })}
        </div>

        {/* Acciones */}
        <div className={`px-5 pb-4 pt-1 flex items-center gap-3 border-t ${k.line}`}>
          <p className={`hidden sm:block text-[11px] ${k.faint}`}>← → para recorrer · Enter para elegir · Esc para salir</p>
          <button type="button" onClick={onClose} className={`ml-auto h-10 px-4 rounded-lg text-sm ${k.iconBtn}`}>Cancelar</button>
          <button type="button" onClick={choose}
            className="h-10 px-5 rounded-lg text-sm font-semibold bg-primary-500 text-gray-900 hover:bg-primary-400 active:scale-[0.98] transition-transform">
            Usar este vehículo
          </button>
        </div>
      </div>
    </div>
  );
}

// El marcador clásico (círculo blanco con flecha), para la opción "Clásico".
function ClassicGlyph({ size }: { size: number }) {
  return (
    <span className="rounded-full bg-primary-500 border-[3px] border-white shadow-lg flex items-center justify-center" style={{ width: size * 0.62, height: size * 0.62 }}>
      <i className="ri-navigation-fill text-white" style={{ fontSize: size * 0.3 }} aria-hidden />
    </span>
  );
}
